/**
 * graft/A6.4 (ADO 288541): the filters a search applied, as user-facing facets.
 *
 * Adapted from the BTNet-Search mock's `buildUnifiedBadges`, with two of its
 * three tiers deliberately dropped:
 *
 *   - MODALITY pills (keyword / semantic / graph) tell a builder how a result
 *     was retrieved. That is our plumbing, not their job.
 *   - ENTITY pills duplicate the entity label the card already shows.
 *
 * What survives is the half a user recognizes — "Payment status: Paid",
 * "amount >= 10000" — which turns this from a debugging lens into ordinary
 * faceted filtering.
 */
import type { ICategorizedHit, ISearchOp } from "./types";

export interface IAppliedFacet {
    /** Stable identity, used for dedup and for selection membership. */
    key: string;
    /** User-facing text. */
    label: string;
    /** Narratives of the ops that produced this facet — the pill's tooltip. */
    narratives: string[];
}

/**
 * The entity is already the card's own label, so an `entity` filter would be a
 * pill that restates the row it sits on.
 */
const REDUNDANT_FILTER_FIELDS = new Set(["entity"]);

const OPERATOR_SYMBOLS: Record<string, string> = {
    eq: "=",
    neq: "!=",
    gt: ">",
    gte: ">=",
    lt: "<",
    lte: "<=",
    in: "in",
    contains: "contains",
    prefix: "starts with",
};

/** `payment_status` → `Payment status`. */
export function humanizeFieldName(field: string): string {
    const spaced = field.replace(/_/g, " ").trim();
    if (spaced.length === 0) return field;
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Whether a raw scalar filter value can be shown to a user as-is.
 *
 * A bare numeral is the tell for an unresolved enum ordinal: the planner is
 * handed `2 (Paid)` and asked to emit `"2"`, so `payment_status = 2` is what
 * reaches the client until the search service attaches `filters_display`
 * (graft/S·1). Rendering that is worse than rendering nothing — it is the only
 * pill on the card now that the modality tier is gone.
 *
 * Operator-shaped filters are handled separately and are NOT subject to this:
 * `amount >= 10000` reads fine precisely because the operator supplies the
 * meaning a bare `10000` lacks.
 */
export function isDisplayableScalar(value: unknown): boolean {
    if (typeof value === "boolean") return true;
    if (typeof value === "number") return false;
    if (typeof value !== "string") return false;
    const trimmed = value.trim();
    if (trimmed.length === 0) return false;
    // Pure integer (optionally signed) — an enum ordinal, not a word.
    return !/^-?\d+$/.test(trimmed);
}

interface IOperatorFilter {
    op?: string;
    value?: unknown;
}

function isOperatorFilter(value: unknown): value is IOperatorFilter {
    return typeof value === "object" && value !== null && !Array.isArray(value) && "value" in value;
}

/**
 * A range bound is only ever a date, number or string in the filter contract.
 * Anything else is malformed, and "?" is a more honest rendering of an open or
 * unusable bound than `[object Object]`.
 */
function formatRangeBound(value: unknown): string {
    if (typeof value === "string") return value.trim().length > 0 ? value : "?";
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    return "?";
}

/**
 * Render one `[field, value]` filter entry as a facet label, or null when it
 * cannot be shown honestly.
 *
 * `filtersDisplay` is the search service's own resolution of the value
 * (graft/S·1) and always wins when present.
 */
export function formatFacetLabel(
    field: string,
    value: unknown,
    filtersDisplay?: Record<string, string>,
): string | null {
    const label = humanizeFieldName(field);

    const resolved = filtersDisplay?.[field];
    if (resolved !== undefined && resolved.trim().length > 0) {
        return `${label}: ${resolved}`;
    }

    if (isOperatorFilter(value)) {
        const op = typeof value.op === "string" ? value.op : "eq";
        if (op === "range" && typeof value.value === "object" && value.value !== null) {
            const range = value.value as { from?: unknown; to?: unknown };
            return `${label}: ${formatRangeBound(range.from)} to ${formatRangeBound(range.to)}`;
        }
        if (Array.isArray(value.value)) {
            const shown = value.value.filter(isDisplayableScalar);
            if (shown.length === 0) return null;
            return `${label} ${OPERATOR_SYMBOLS[op] ?? op} ${shown.join(", ")}`;
        }
        // A comparison reads fine with a bare number — the operator carries
        // the meaning that a lone ordinal lacks.
        if (op !== "eq" && (typeof value.value === "number" || typeof value.value === "string")) {
            return `${label} ${OPERATOR_SYMBOLS[op] ?? op} ${value.value}`;
        }
        return isDisplayableScalar(value.value) ? `${label}: ${String(value.value)}` : null;
    }

    if (Array.isArray(value)) {
        const shown = value.filter(isDisplayableScalar);
        if (shown.length === 0) return null;
        return `${label}: ${shown.join(", ")}`;
    }

    return isDisplayableScalar(value) ? `${label}: ${String(value)}` : null;
}

/** Facets contributed by a single search operation. */
function facetsForOp(op: ISearchOp): IAppliedFacet[] {
    if (!op.filters || typeof op.filters !== "object") return [];
    const narrative = op.narrative || op.id;
    const facets: IAppliedFacet[] = [];

    for (const [field, value] of Object.entries(op.filters)) {
        if (REDUNDANT_FILTER_FIELDS.has(field)) continue;
        const label = formatFacetLabel(field, value, op.filters_display);
        if (label === null) continue;
        facets.push({ key: label, label, narratives: narrative ? [narrative] : [] });
    }

    return facets;
}

/**
 * The deduped facets for one hit, across every op that surfaced it. Two ops
 * that applied the same filter produce one pill carrying both narratives.
 */
export function buildAppliedFacets(hit: ICategorizedHit): IAppliedFacet[] {
    const byKey = new Map<string, IAppliedFacet>();

    for (const op of hit.sourceOps ?? []) {
        for (const facet of facetsForOp(op)) {
            const existing = byKey.get(facet.key);
            if (existing) {
                for (const n of facet.narratives) {
                    if (!existing.narratives.includes(n)) existing.narratives.push(n);
                }
            } else {
                byKey.set(facet.key, { ...facet, narratives: [...facet.narratives] });
            }
        }
    }

    return [...byKey.values()];
}

/** The facet keys a hit carries — the membership test for selection. */
export function facetKeysForHit(hit: ICategorizedHit): Set<string> {
    return new Set(buildAppliedFacets(hit).map((f) => f.key));
}

/**
 * Narrow a list to hits carrying EVERY selected facet. Selecting two facets
 * intersects rather than unions: each click is "and also this", which is what
 * clicking a pill on a row you already like means.
 */
export function filterHitsByFacets<T extends ICategorizedHit>(
    hits: T[],
    selected: ReadonlySet<string>,
): T[] {
    if (selected.size === 0) return hits;
    return hits.filter((hit) => {
        const keys = facetKeysForHit(hit);
        for (const key of selected) {
            if (!keys.has(key)) return false;
        }
        return true;
    });
}
